import React, { useState, useEffect, useCallback, Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import { PageTranslator, MainLangSwitch, UI_LANGS as SITE_LANGS } from "./components/MainPageTranslator";

const LangFallback = () => {
  const { pathname } = useLocation();
  return <Navigate to={pathname.replace(/^\/(es|de|fr|it|nl|ro|sv|no|da)(?=\/|$)/, '') || '/'} replace />;
};
import axios from "axios";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";

// Lazy-loaded page components (code-split for performance)
const WinesPage = lazy(() => import("./components/WinesPage"));
const BeersPage = lazy(() => import("./components/BeersPage"));
const ContactPage = lazy(() => import("./components/ContactPage"));
const SpiritsPage = lazy(() => import("./components/SpiritsPage"));
const LocalProductsPage = lazy(() => import("./components/LocalProductsPage"));
const SecureAdminPanel = lazy(() => import("./components/SecureAdminPanel"));
const ProductGallery = lazy(() => import("./components/ProductGallery"));
const POSDashboard = lazy(() => import("./components/POSDashboard"));
const ReceiptPage = lazy(() => import("./components/ReceiptPage"));
const GiftCardPage = lazy(() => import("./components/GiftCardPage"));
const AuthCallback = lazy(() => import("./components/AuthCallback"));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./components/TermsConditions'));
const BlogPage = lazy(() => import("./components/BlogPage"));
const BlogHub = lazy(() => import("./components/BlogHub"));
const TranslatedBlogPost = lazy(() => import("./components/BlogExtras"));
const ProductPage = lazy(() => import("./components/ProductPage"));
const ProductsIndex = lazy(() => import("./components/ProductPage").then(m => ({ default: m.ProductsIndex })));
const FAQPage = lazy(() => import("./components/FAQPage"));
const WineBrandPage = lazy(() => import("./components/WineBrandPage"));
const WineTastingPage = lazy(() => import("./components/WineTastingPage"));
const BeerBrandPage = lazy(() => import("./components/BeerBrandPage"));
const RefundPolicy = lazy(() => import("./components/RefundPolicy"));

// Direct imports (small, used on every page)
import SEO from './components/SEO';
import StickyCtaBar from "./components/StickyCtaBar";
import TrustBadges from "./components/TrustBadges";
import GoogleReviewCTA from "./components/GoogleReviewCTA";
import FeaturedPicks from "./components/FeaturedPicks";
import { proxyImage, proxyImages } from './utils/imageProxy';
import MultilingualSpeakable from './components/MultilingualSpeakable';
import { buildMultilingualFaqSchemas } from './utils/multilingualSEO';
import ChatWidget from './components/ChatWidget';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Newsletter Signup Widget Component
const NewsletterWidget = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [isExitIntent, setIsExitIntent] = useState(false);

  useEffect(() => {
    // Don't show on admin, POS, or receipt routes
    const path = window.location.pathname;
    if (path.startsWith('/admin') || path.startsWith('/pos') || path.startsWith('/receipt') || path.startsWith('/gift-card')) {
      return;
    }

    // Show widget after 3 seconds
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 3000);

    // Exit intent detection
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !isExpanded) {
        setIsExitIntent(true);
        setIsExpanded(true);
      }
    };

    // Listen for footer button click
    const handleShowNewsletterWidget = () => {
      setIsVisible(true);
      setIsExpanded(true);
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('showNewsletterWidget', handleShowNewsletterWidget);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('showNewsletterWidget', handleShowNewsletterWidget);
    };
  }, [isExpanded]);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/newsletter-subscribe`, {
        first_name: firstName,
        last_name: lastName,
        email: email
      });
      
      toast.success(`Welcome ${firstName}! You've been added to our newsletter.`);
      setFirstName('');
      setLastName('');
      setEmail('');
      setIsExpanded(false);
      
      // Hide widget after successful signup
      setTimeout(() => {
        setIsVisible(false);
      }, 2000);
    } catch (error) {
      toast.error("Failed to subscribe. Please try again.");
      console.error('Newsletter signup error:', error);
    }
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  if (!isVisible) return null;

  return (
    <div className={`fixed bottom-4 right-4 z-50 transition-all duration-300 ${
      isExpanded ? 'w-80' : 'w-16 h-16'
    }`}>
      {!isExpanded ? (
        // Compact widget
        <button
          onClick={toggleExpanded}
          className="w-16 h-16 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
          title="Subscribe to Newsletter"
        >
          <span className="text-2xl">📧</span>
        </button>
      ) : (
        // Expanded newsletter form
        <div className="bg-white rounded-lg shadow-2xl p-4 border-2 border-blue-600">
          <div className="flex justify-between items-center mb-3">
            <p className="text-lg font-bold text-blue-600">Newsletter</p>
            <button
              onClick={() => setIsExpanded(false)}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              ×
            </button>
          </div>
          
          {isExitIntent && (
            <p className="text-sm text-red-600 mb-3 font-semibold">
              Wait! Don't miss our latest offers and local news!
            </p>
          )}
          
          <p className="text-sm text-gray-600 mb-4">
            Get exclusive offers, wine recommendations, and local Lanzarote insights!
          </p>
          
          <form onSubmit={handleNewsletterSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="text"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                className="text-sm h-8"
              />
              <Input
                type="text"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                className="text-sm h-8"
              />
            </div>
            <Input
              type="email"
              placeholder="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="text-sm h-8"
            />
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 h-8 text-sm">
              Subscribe Now
            </Button>
          </form>
          
          <p className="text-xs text-gray-500 mt-2">
            Unsubscribe anytime. We respect your privacy.
          </p>
        </div>
      )}
    </div>
  );
}

// Counter Animation Hook
const useCountUp = (end, duration = 2000, start = 0) => {
  const [count, setCount] = useState(start);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      setCount(Math.floor(progress * (end - start) + start));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [end, duration, start, isVisible]);

  const ref = useCallback((node) => {
    if (node !== null) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        },
        { threshold: 0.1 }
      );
      observer.observe(node);
    }
  }, []);

  return [count, ref];
};

// Photo Gallery Component — shop images with Facebook CTA
const PhotoGallery = () => {
  const galleryPhotos = [
    { image: IMAGES.marketing6, caption: "Lanzarote wine and terrace vibes at Brunchi" },
    { image: IMAGES.fridgeBeer, caption: "Malpeis & NAO craft beers chilled and ready" },
    { image: IMAGES.wineSelection, caption: "100+ Lanzarote volcanic wines on our wall" },
    { image: IMAGES.cocktails, caption: "Cocktails and sangria on the sunny terrace" },
    { image: IMAGES.marketing7, caption: "Lanzarote craft beer on the terrace" },
    { image: IMAGES.spiritsWall, caption: "800+ spirits — whisky, gin, rum and more" }
  ];

  return (
    <div className="py-12" data-testid="photo-gallery-section">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-3">
          <div className="w-16 h-16 rounded-full bg-blue-600 p-[3px]">
            <div className="w-full h-full rounded-full bg-white p-[2px]">
              <img src={IMAGES.logo} alt="Brunchi.shop — wine, beer & spirits shop Playa Blanca Lanzarote" title="Brunchi.shop" width="56" height="56" className="w-full h-full rounded-full object-cover" />
            </div>
          </div>
          <div className="text-left">
            <h3 className="text-xl font-bold text-gray-900">Inside the Shop</h3>
            <p className="text-sm text-gray-500">Marina Rubicon, Playa Blanca</p>
          </div>
        </div>
      </div>
      
      {/* Photo Grid */}
      <div className="max-w-5xl mx-auto px-4">
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-1 sm:gap-2">
          {galleryPhotos.map((post, index) => (
            <div
              key={index}
              className="group aspect-square overflow-hidden relative bg-gray-100"
              data-testid={`gallery-photo-${index}`}
            >
              <img
                loading={index < 4 ? undefined : "lazy"}
                decoding="async"
                src={post.image}
                alt={`${post.caption} — Brunchi.shop, Marina Rubicon Playa Blanca Lanzarote`}
                title={`Brunchi.shop — ${post.caption}`}
                width="400" height="400"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white text-center px-3">
                  <p className="text-xs font-medium leading-tight">{post.caption}</p>
                </div>
              </div>
            </div>
          ))}
          {/* Last cell — Facebook CTA */}
          <a
            href="https://www.facebook.com/brunchi.shop"
            target="_blank"
            rel="noopener noreferrer"
            className="aspect-square flex flex-col items-center justify-center bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            data-testid="facebook-follow-cta"
          >
            <svg className="w-10 h-10 mb-2" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            <span className="text-sm font-bold">Follow us</span>
            <span className="text-xs opacity-80">on Facebook</span>
          </a>
        </div>
      </div>

      <p className="text-center text-xs text-gray-400 mt-4">All photography by Brunchi.shop, Marina Rubicon, Playa Blanca, Lanzarote</p>
    </div>
  );
};
const _APP_IMAGES = {
  logo: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/vzljiqc8_Gifts-Wine.png",
  hero: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/vbzvxphp_IMG_8009-scaled.jpeg",
  teamPhoto: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/wkag9wrk_generated-image-2025-09-08-2.jpg.webp",
  bookCover: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/7bd4lv4z_Discover-the-Flavours-of-Lanzarote-Generic.jpg",
  cocktails: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/py0fjf39_30b91c98-af84-4a3e-b030-506d4215cac5.jpeg",
  giftBaskets: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/d8twuvzp_IMG_5855-scaled.jpeg.webp",
  luxuryHamper: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/aa6p0hbp_luxurious-gift-basket-featuring-champagne-and-chocolates-wrapped-in-cellophane-with-ribbon.-27393960-scaled.jpg.webp",
  spiritsWall: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/9v5y4ryl_6D4ACE4B-0107-4C36-B439-21529139190F-scaled.jpeg.webp",
  wineSelection: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/5u8z7fb5_IMG_8856.jpeg",
  fridgeBeer: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/n222zcrj_IMG_8882.jpeg",
  // New marketing images
  marketing1: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/mxcwrec4_IMG_2529.jpeg",
  marketing2: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/wwljhpxz_IMG_2530.jpeg", 
  marketing3: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/ea3jqxyp_IMG_2531.jpeg",
  marketing4: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/9fqmciz9_IMG_2532.jpeg",
  marketing5: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/s686cjr8_IMG_2533.jpeg",
  marketingWinesBeer: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/7ne3k41k_Discover%20Lanzarote%27s%20Finest%20Wines%20%26%20Craft%20Beers%20By%20the%20glass%20or%20bottle%2C%20enjoy%20local%20flavours%20on%20our%20terrace.%20%28740%20x%20700%20px%29.jpg",
  marketing6: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/pgt3d9ug_1.jpg",
  marketing7: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/qo125er4_2.jpg",
  marketing8: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/oc1xrp1q_3.jpg",
  marketing9: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/29yiwxcl_4.jpg"
};
const IMAGES = proxyImages(_APP_IMAGES, 600);

// Yaiza Wine Collection with NEW CORRECTED Images + Tinto
const yaizaWines = [
  {
    name: "Yaiza Blanco Seco",
    variety: "Malvasía Volcánica",
    description: "Crisp, dry white wine with volcanic minerality and citrus notes.",
    price: "€19.50",
    glassPrice: "€6.00",
    image: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/wwljhpxz_IMG_2530.jpeg" // Keep current Seco
  },
  {
    name: "Yaiza Blanco Semidulce", 
    variety: "Malvasía Volcánica",
    description: "Semi-sweet white wine with delicate fruit flavours and volcanic character.",
    price: "€19.50",
    glassPrice: "€6.00",
    image: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/9fqmciz9_IMG_2532.jpeg" // NEW Semi-dulce image
  },
  {
    name: "Yaiza Blanco Afrutado",
    variety: "Moscatel Liego", 
    description: "Fruity white wine with aromatic moscatel character and tropical notes.",
    price: "€19.50",
    glassPrice: "€6.00",
    image: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/ea3jqxyp_IMG_2531.jpeg" // Keep current Afrutado
  },
  {
    name: "Yaiza Rosado",
    variety: "Listán Negro",
    description: "Elegant rosé with fresh berry flavours and volcanic complexity.",
    price: "€19.50", 
    glassPrice: "€6.00",
    image: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/s686cjr8_IMG_2533.jpeg" // NEW Rosado image
  },
  {
    name: "Yaiza Tinto",
    variety: "Listán Negro",
    description: "Full-bodied red wine with rich volcanic character, dark fruit flavours and earthy undertones.",
    price: "€22.00", 
    glassPrice: "€6.50",
    image: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/m8mow2ln_IMG_2535.jpeg" // NEW Tinto wine
  }
];

// Malpeis Beer Collection - Only Available Beers
const malpeisBeers = [
  {
    name: "Despiste New Zealand Hoppy Ale",
    description: "A hoppy, hazy, and warm beer with a golden hue and spicy, orange peel-like flavours.",
    specs: "5% Vol | 9 EBC | 18 IBUs",
    price: "€4.50",
    image: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/yn1xqi25_ede6069d-c542-48db-a1df-21ac97900d89.jpeg"
  },
  {
    name: "La Fresca Session IPA",
    description: "Pale yellow, slightly cloudy beer, with hoppy aromas bringing to mind tropical and citrus fruits. Very light and refreshing.",
    specs: "4,5 % V | 25 ibus | 6 EBC",
    price: "€4.50",
    image: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/15l2kv76_Screenshot-2025-03-10-at-14.21.05.png.webp"
  },
  {
    name: "Rofe Bock",
    description: "Beer with fine bubbly, creamy foam, dark brown in colour. Rich malty flavour with light notes of caramel and coffee.",
    specs: "7 % V | 22 ibus | 55 EBC",
    price: "€4.50",
    image: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/t6nhc0af_Screenshot-2025-03-10-at-14.20.52.png"
  },
  {
    name: "Bermeja Red IPA",
    description: "Amber-coloured beer with creamy, persistent foam. Remarkable hop aroma, with hints of peach and apricot, intense and fruity in the mouth.",
    specs: "6,7 % V | 48 ibus | 26 EBC",
    price: "€4.50",
    image: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/j7ujtzge_Screenshot-2025-09-04-at-00.35.22.png"
  },
  {
    name: "Chaisss! Hazy IPA",
    description: "Cloudy beer, with a dense and creamy head. Ripe stone fruit and citrus flavours such as orange and tangerine.",
    specs: "6,2 % V | 38 ibus | 13 EBC", 
    price: "€4.50",
    image: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/4rs5tx3j_Screenshot-2025-09-04-at-09.34.24.png.webp"
  },
  {
    name: "Jable Blond Ale",
    description: "Golden-coloured beer with light foam and sweet malty aromas. Its medium body and low bitterness make it pleasant and easy to drink.",
    specs: "5 % V | 18 ibus | 7,5 EBC",
    price: "€4.50",
    image: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/wbt1y12r_Screenshot-2025-09-04-at-00.28.07.png"
  },
  {
    name: "Palesita (The Outsider)",
    description: "Amber-coloured toasted beer, with hints of toast and biscuits, medium/medium-low body and a dry finish in the mouth, making it easy to drink.",
    specs: "4,8 % V | 36 ibus | 18 EBC",
    tagline: "The Outsider",
    price: "€4.50",
    image: "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/ojrg9rs3_Screenshot-2025-09-04-at-10.29.29.png.webp"
  }
];

// Blog Posts
const blogPosts = [];

// Combined Cookie Consent & Age Verification Component
const CookieConsentAndAgeVerification = () => {
  const [showConsent, setShowConsent] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);

  useEffect(() => {
    // Don't show on admin, POS, or receipt routes
    const path = window.location.pathname;
    if (path.startsWith('/admin') || path.startsWith('/pos') || path.startsWith('/receipt') || path.startsWith('/gift-card')) {
      return;
    }

    const consent = localStorage.getItem('brunchi-cookie-consent');
    const ageVerified = localStorage.getItem('brunchi-age-verified');
    
    // Show popup if either consent is missing
    if (!consent || !ageVerified) {
      setShowConsent(true);
    }
  }, []);

  const handleAcceptAll = () => {
    if (!ageConfirmed) {
      toast.error("Please confirm you are of legal drinking age to continue.");
      return;
    }
    
    localStorage.setItem('brunchi-cookie-consent', 'accepted');
    localStorage.setItem('brunchi-age-verified', 'confirmed');
    localStorage.setItem('brunchi-consent-timestamp', new Date().toISOString());
    setShowConsent(false);
    toast.success("Thank you for confirming. Enjoy exploring our premium collection!");
  };

  const handleRejectCookies = () => {
    if (!ageConfirmed) {
      toast.error("Age verification is required to access this website.");
      return;
    }
    
    localStorage.setItem('brunchi-cookie-consent', 'rejected');
    localStorage.setItem('brunchi-age-verified', 'confirmed');
    localStorage.setItem('brunchi-consent-timestamp', new Date().toISOString());
    setShowConsent(false);
    toast.success("Age verified. Cookies rejected. You can still explore our collection!");
  };

  if (!showConsent) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[200] p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-2xl border-2 border-blue-600">
        <div className="text-center mb-6">
          <div className="flex justify-center items-center gap-3 mb-4">
            <span className="text-3xl">🍪</span>
            <span className="text-3xl">🥃</span>
            <span className="text-3xl">⚠️</span>
          </div>
          <p className="text-2xl font-bold text-gray-800 mb-2">Cookie Consent & Age Verification</p>
        </div>

        <div className="space-y-4 mb-6">
          {/* Cookie Consent Section */}
          <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-600">
            <p className="font-semibold text-blue-800 mb-2">Cookie Usage</p>
            <p className="text-sm text-gray-700">
              We use cookies to enhance your browsing experience, analyze traffic, and remember your preferences. 
              <Link to="/privacy-policy" className="text-blue-600 hover:underline ml-1 font-semibold">
                Learn more in our Privacy Policy
              </Link>
            </p>
          </div>

          {/* Age Verification Section */}
          <div className="bg-amber-50 p-4 rounded-lg border-l-4 border-amber-600">
            <p className="font-semibold text-amber-800 mb-2">Age Verification Required</p>
            <p className="text-sm text-gray-700 mb-3">
              This website contains information about alcoholic beverages. You must be of legal drinking age to continue:
            </p>
            <ul className="text-xs text-gray-600 mb-3">
              <li>• <strong>Spain/EU:</strong> 18+ years old</li>
              <li>• <strong>United States:</strong> 21+ years old</li>
              <li>• <strong>Other countries:</strong> As required by local law</li>
            </ul>
            
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="age-verification"
                checked={ageConfirmed}
                onChange={(e) => setAgeConfirmed(e.target.checked)}
                className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                required
              />
              <label htmlFor="age-verification" className="text-sm font-semibold text-gray-800">
                <span className="text-red-600">*</span> I confirm I am of legal drinking age in my jurisdiction
              </label>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button 
            variant="outline" 
            onClick={handleRejectCookies} 
            disabled={!ageConfirmed}
            className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirm Age & Reject Cookies
          </Button>
          <Button 
            onClick={handleAcceptAll}
            disabled={!ageConfirmed}
            className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirm Age & Accept All
          </Button>
        </div>

        <div className="mt-4 text-center">
          <p className="text-xs text-gray-500">
            By continuing, you agree to our{' '}
            <Link to="/terms" className="text-blue-600 hover:underline">Terms & Conditions</Link>
            {' '}and confirm you are of legal drinking age.
          </p>
          <p className="text-xs text-gray-500 mt-1">
            We do not knowingly collect information from individuals under the legal drinking age.
          </p>
        </div>
      </div>
    </div>
  );
};

// Book Download Popup Component
const BookDownloadPopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);

  useEffect(() => {
    // Don't show on admin, POS, or receipt routes
    const path = window.location.pathname;
    if (path.startsWith('/admin') || path.startsWith('/pos') || path.startsWith('/receipt') || path.startsWith('/gift-card')) {
      return;
    }

    const hasShown = localStorage.getItem('brunchi-book-popup-shown');
    if (!hasShown) {
      const timer = setTimeout(() => {
        setShowPopup(true);
        localStorage.setItem('brunchi-book-popup-shown', 'true');
      }, 5000);
      return () => clearTimeout(timer);
    }

    // Listen for custom event to show popup from other components
    const handleShowBookDownload = () => {
      setShowPopup(true);
    };

    window.addEventListener('showBookDownload', handleShowBookDownload);
    return () => window.removeEventListener('showBookDownload', handleShowBookDownload);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate terms agreement
    if (!agreedToTerms) {
      toast.error("Please agree to our Terms & Conditions to proceed.");
      return;
    }
    
    try {
      // Submit details for newsletter and get download link
      await axios.post(`${API}/book-download`, { 
        first_name: firstName,
        last_name: lastName,
        email: email,
        agreed_to_terms: agreedToTerms,
        marketing_consent: marketingConsent
      });
      
      // Get direct download link and trigger download
      const downloadResponse = await axios.get(`${API}/book-download-direct`);
      const downloadUrl = downloadResponse.data.download_url;
      
      // Create download link and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = downloadResponse.data.filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      const consentMsg = marketingConsent 
        ? "Your download has started and welcome email is on the way. You've been added to our newsletter!"
        : "Your download has started and welcome email is on the way.";
      
      toast.success(`Welcome ${firstName}! ${consentMsg}`);
      setFirstName('');
      setLastName('');
      setEmail('');
      setAgreedToTerms(false);
      setMarketingConsent(false);
      setShowPopup(false);
    } catch (error) {
      toast.error("Failed to download book. Please try again.");
      console.error('Download error:', error);
    }
  };

  if (!showPopup) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[90] p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6 relative shadow-2xl">
        <button 
          onClick={() => setShowPopup(false)}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl"
        >
          ×
        </button>
        
        <div className="text-center">
          <img loading="lazy" decoding="async" 
            src={IMAGES.bookCover}
            alt="Free download — The Flavours of Lanzarote guide by Brunchi.shop, with local wines, spirits and foodie tips"
            title="The Insider's Guide to Lanzarote's Best Flavours — Free Download"
            width="128" height="180"
            className="w-32 mx-auto mb-4 rounded-lg shadow-lg"
          />
          <p className="text-xs font-bold text-amber-600 uppercase tracking-widest mb-1">Free Instant Download</p>
          <p className="text-xl font-bold mb-2 text-gray-900">The Insider's Guide to<br/>Lanzarote's Best Flavours</p>
          <p className="text-sm text-gray-600 mb-1">Handpicked local wines, rare island spirits, and hidden foodie gems — all from the team at Brunchi.</p>
          <p className="text-xs text-amber-700 font-semibold mb-5">Trusted by visitors & locals since 2024</p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Input
                type="text"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                className="w-full"
              />
              <Input
                type="text"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                className="w-full"
              />
            </div>
            <Input
              type="email"
              placeholder="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full"
            />
            
            {/* GDPR Compliant Checkboxes */}
            <div className="space-y-3 text-left">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="terms-checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  required
                />
                <label htmlFor="terms-checkbox" className="text-sm text-gray-700">
                  <span className="font-semibold text-red-600">*</span> I agree to the{' '}
                  <Link to="/terms" className="text-blue-600 underline" target="_blank">
                    Terms & Conditions
                  </Link>
                </label>
              </div>
              
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="marketing-checkbox"
                  checked={marketingConsent}
                  onChange={(e) => setMarketingConsent(e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="marketing-checkbox" className="text-sm text-gray-700">
                  I would like to receive marketing emails with news, offers, and updates about Brunchi.shop
                </label>
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-3 text-base"
              disabled={!agreedToTerms}
            >
              Get My Free Guide
            </Button>
          </form>
          
          <div className="text-[10px] text-gray-400 mt-4 leading-relaxed">
            <p>Your data is safe with us. Read our{' '}
              <Link to="/privacy-policy" className="text-blue-500 underline">Privacy Policy</Link>.
              {' '}Unsubscribe anytime.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Autumn/Winter Terrace Specials Ticker
const SeasonalTicker = () => {
  return (
    <div className="fixed top-0 left-0 right-0 z-[60] overflow-hidden bg-amber-500" data-testid="seasonal-ticker">
      <div className="flex animate-ticker-seasonal whitespace-nowrap py-2.5">
        {[...Array(4)].map((_, i) => (
          <span key={i} className="inline-flex items-center mx-6 text-sm tracking-wide">
            <span className="text-black font-extrabold uppercase">Autumn & Winter Terrace Specials</span>
            <span className="text-red-900 mx-3 text-lg">&#9733;</span>
            <span className="text-black font-medium">Tinto Vermouth with Seasonal Orange & Olives</span>
            <span className="text-red-900 mx-3 text-lg">&#9733;</span>
            <span className="text-black font-medium">Our Sangria? </span><span className="text-red-900 font-extrabold italic">&nbsp;Spectacular — the best they've ever had</span>
            <span className="text-red-900 mx-3 text-lg">&#9733;</span>
            <span className="text-black font-medium">Warming Whisky Flights for Chillier Evenings</span>
            <span className="text-red-900 mx-3 text-lg">&#9733;</span>
            <span className="text-black font-medium">Brandy by the Glass on the Terrace</span>
            <span className="text-red-900 mx-3 text-lg">&#9733;</span>
          </span>
        ))}
      </div>
      <style>{`
        @keyframes tickerSeasonal { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-ticker-seasonal { animation: tickerSeasonal 35s linear infinite; }
      `}</style>
    </div>
  );
};

// Navigation Component with Logo
const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setIsMenuOpen(false), [pathname]);

  return (
    <nav className="fixed top-10 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-blue-100 shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center space-x-3">
            <img loading="lazy" decoding="async" 
              src={IMAGES.logo} 
              alt="Brunchi.shop logo — wine, spirits and craft beer shop at Marina Rubicon, Playa Blanca, Lanzarote"
              title="Brunchi.shop"
              width="40" height="40"
              className="h-10 w-10"
            />
            <span className="text-xl font-bold text-blue-600">Brunchi.shop</span>
          </Link>
          
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/wine-tasting" className="text-gray-700 hover:text-blue-600 transition-colors font-semibold">Wine Tasting</Link>
            <Link to="/wines" className="text-gray-700 hover:text-blue-600 transition-colors">Wines</Link>
            <Link to="/beers" className="text-gray-700 hover:text-blue-600 transition-colors">Craft Beers</Link>
            <Link to="/spirits" className="text-gray-700 hover:text-blue-600 transition-colors">Products</Link>
            <Link to="/blog" className="text-gray-700 hover:text-blue-600 transition-colors">Blog</Link>
            <Link to="/faq" className="text-gray-700 hover:text-blue-600 transition-colors">FAQ</Link>
            <Link to="/contact" className="text-gray-700 hover:text-blue-600 transition-colors">Contact</Link>
            <Button asChild className="bg-blue-600 hover:bg-blue-700 text-white">
              <a href="mailto:consultas@brunchi.shop?subject=Brunchi.shop%20enquiry">Email Us</a>
            </Button>
          </div>
          
          <button 
            className="md:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <div className="w-6 h-6 flex flex-col justify-center items-center">
              <div className={`w-5 h-0.5 bg-gray-700 transition-all ${isMenuOpen ? 'rotate-45 translate-y-1' : ''}`}></div>
              <div className={`w-5 h-0.5 bg-gray-700 transition-all mt-1 ${isMenuOpen ? '-rotate-45 -translate-y-1' : ''}`}></div>
            </div>
          </button>
        </div>
        
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t py-4">
            <div className="flex flex-col space-y-4">
              <Link to="/wine-tasting" className="text-gray-700 hover:text-blue-600 font-semibold">Wine Tasting</Link>
              <Link to="/wines" className="text-gray-700 hover:text-blue-600">Wines</Link>
              <Link to="/beers" className="text-gray-700 hover:text-blue-600">Craft Beers</Link>
              <Link to="/spirits" className="text-gray-700 hover:text-blue-600">Products</Link>
              <Link to="/blog" className="text-gray-700 hover:text-blue-600">Blog</Link>
              <Link to="/faq" className="text-gray-700 hover:text-blue-600">FAQ</Link>
              <Link to="/contact" className="text-gray-700 hover:text-blue-600">Contact</Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Homepage Component
const HomePage = () => {
  const [count1, ref1] = useCountUp(90, 2000);
  const [count2, ref2] = useCountUp(30, 2000);
  const [count3, ref3] = useCountUp(25, 2000);
  const [count4, ref4] = useCountUp(6500, 3000);

  return (
    <div className="min-h-screen">
      <SEO
        title="Brunchi.shop | Yaiza Wine Tasting & Craft Beer | Playa Blanca"
        description="Yaiza wine tasting from €14, 800+ spirits. Terrace bar at Marina Rubicon. Open Mon-Sat, walk in any time."
        keywords="yaiza wine tasting, yaiza wine, malpeis craft beer, nao craft beer, wine shop playa blanca, craft beer lanzarote, spirits shop marina rubicon, wine tasting lanzarote, terrace bar marina rubicon, lanzarote wine, volcanic wine, blue bottle wine lanzarote, things to do playa blanca, evening drinks marina rubicon, best bar playa blanca, wine shop lanzarote, gara wine lanzarote"
        canonical="/"
        image="https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/vbzvxphp_IMG_8009-scaled.jpeg"
        speakable={[".speakable-answer", "h1", "h2"]}
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": "https://brunchi.shop/#business",
            "name": "Brunchi.shop",
            "alternateName": "Brunchi Gourmet & Artisan",
            "description": "Family-run wine, spirits and craft beer shop with a sunny terrace bar at Marina Rubicon, Playa Blanca, Lanzarote.",
            "url": "https://brunchi.shop",
            "email": "consultas@brunchi.shop",
            "image": "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/vbzvxphp_IMG_8009-scaled.jpeg",
            "logo": "https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/vbzvxphp_IMG_8009-scaled.jpeg",
            "priceRange": "€€",
            "currenciesAccepted": "EUR",
            "paymentAccepted": "Cash, Credit Card, Debit Card",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Calle El Burrugo 44B, Marina Rubicon",
              "addressLocality": "Playa Blanca",
              "addressRegion": "Lanzarote, Canary Islands",
              "postalCode": "35580",
              "addressCountry": "ES"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 28.8575,
              "longitude": -13.8169
            },
            "openingHoursSpecification": [
              {"@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"], "opens": "10:00", "closes": "15:00"}, {"@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"], "opens": "18:00", "closes": "22:00"}
            ],
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Brunchi Products & Experiences",
              "itemListElement": [
                {"@type": "Offer", "itemOffered": {"@type": "Product", "name": "Lanzarote Wine Tasting (4 glasses)", "offers": {"@type": "Offer", "price": "14.00", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "hasMerchantReturnPolicy": {"@id": "https://brunchi.shop/#return-policy"}}, "sku": "BRUNCHI-WINETASTING-4", "brand": {"@type": "Brand", "name": "Brunchi.shop"}, "category": "Wine Tasting Experience", "description": "Choose a Yaiza Board (Rosado, Seco, Semi-Dulce, Afrutado) or Red Board (Yaiza Tinto, Volcano, Ribera, Rioja) — or mix and match", "image": "https://customer-assets.emergentagent.com/job_6abba286-c4a7-450f-8f93-020d629f80c9/artifacts/zxttut82_7.png", "aggregateRating": {"@type": "AggregateRating", "ratingValue": "5.0", "reviewCount": "44", "bestRating": "5", "worstRating": "1"}, "review": [{"@type": "Review", "author": {"@type": "Person", "name": "James W"}, "datePublished": "2025-11-20", "reviewRating": {"@type": "Rating", "ratingValue": "5", "bestRating": "5"}, "reviewBody": "Wine tasting of the local Yaiza wine, the famous blue bottles. Great value at 14 euros for 4 glasses."}]}, "price": "14.00", "priceCurrency": "EUR", "url": "https://brunchi.shop/wine-tasting"},
              ]
            },
            "sameAs": [
              "https://www.facebook.com/brunchi.shop/",
              "https://www.tripadvisor.com/Restaurant_Review-g659633-d28127915-Reviews-Brunchi-Playa_Blanca_Lanzarote_Canary_Islands.html",
              "https://www.google.com/maps/place/Brunchi/@28.8575,-13.8169,17z/"
            ],
            "areaServed": [
              {"@type": "City", "name": "Playa Blanca"},
              {"@type": "AdministrativeArea", "name": "Lanzarote"},
              {"@type": "AdministrativeArea", "name": "Canary Islands"}
            ],
            "founder": [
              {"@type": "Person", "name": "Florin"}
            ],
            "knowsLanguage": ["en", "fr", "de", "it", "nl", "es", "ro"],
            "slogan": "Local Flavours. Global Inspiration. Personal Touch.",
            "potentialAction": {
              "@type": "OrderAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": "mailto:consultas@brunchi.shop?subject=Brunchi.shop%20enquiry",
                "actionPlatform": "http://schema.org/MobileWebPlatform"
              }
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "52",
              "bestRating": "5",
              "worstRating": "1"
            },
            "review": [
              {"@type": "Review", "author": {"@type": "Person", "name": "Ange L"}, "datePublished": "2025-12-07", "reviewRating": {"@type": "Rating", "ratingValue": "5", "bestRating": "5"}, "reviewBody": "We are regulars at Brunchi and for good reason. It is a calm little corner of Marina Rubicon where you can try and buy Lanzarote wines without any pressure, just good guidance and a relaxed vibe."},
              {"@type": "Review", "author": {"@type": "Person", "name": "Rob W"}, "datePublished": "2025-09-16", "reviewRating": {"@type": "Rating", "ratingValue": "5", "bestRating": "5"}, "reviewBody": "Great bar/shop on the marina. The choice of drinks is incredible, including some excellent craft beers. Florin, the owner, is a really engaging and welcoming person."},
              {"@type": "Review", "author": {"@type": "Person", "name": "James W"}, "datePublished": "2025-11-20", "reviewRating": {"@type": "Rating", "ratingValue": "5", "bestRating": "5"}, "reviewBody": "Really relaxing atmosphere and comfortable seating. We had a wine tasting of the local Yaiza wine, the famous blue bottles. Great value and Florin was very knowledgeable."}
            ],
            "foundingDate": "2024",
            "hasMap": "https://www.google.com/maps/place/Brunchi/@28.8575,-13.8169,17z/",
            "contactPoint": [
              {"@type": "ContactPoint", "email": "consultas@brunchi.shop", "contactType": "customer service", "availableLanguage": ["English", "Spanish", "French", "German", "Italian", "Dutch", "Romanian"]},
              {"@type": "ContactPoint", "email": "consultas@brunchi.shop", "contactType": "sales", "availableLanguage": ["English", "Spanish"]}
            ]
          },
          ...buildMultilingualFaqSchemas('home'),
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "inLanguage": "en",
            "mainEntity": [
              {"@type": "Question", "name": "Where can I buy wine in Playa Blanca, Lanzarote?", "acceptedAnswer": {"@type": "Answer", "text": "Brunchi.shop at Marina Rubicon is the best wine shop in Playa Blanca, offering over 100 wines including iconic Yaiza blue-bottle wines (from €21.50/bottle), El Grifo, Bermejo and La Geria volcanic wines. Walk-in wine tasting — 4 glasses for €14. Open Monday to Saturday 10am-3pm and 6pm-10pm, closed Sundays."}},
              {"@type": "Question", "name": "What days is Marina Rubicon market in Playa Blanca?", "acceptedAnswer": {"@type": "Answer", "text": "The Marina Rubicon market runs every Wednesday and Saturday from 9:00 AM to 2:00 PM. You'll find handmade crafts, jewellery, leather goods, local wines, aloe vera products and souvenirs. Brunchi.shop is open on market days and every other day too."}},
              {"@type": "Question", "name": "Where can I do wine tasting in Playa Blanca without a tour?", "acceptedAnswer": {"@type": "Answer", "text": "Brunchi.shop at Marina Rubicon offers walk-in wine tasting every day — no tour or booking needed. For €14 you get 4 glasses of Lanzarote wines. Choose the Yaiza Board (Rosado, Seco, Semi-Dulce, Afrutado) or Red Board (Yaiza Tinto, Volcano, Ribera, Rioja). Enjoy on the sunny terrace."}},
              {"@type": "Question", "name": "How much does wine tasting cost in Playa Blanca?", "acceptedAnswer": {"@type": "Answer", "text": "Wine tasting at Brunchi.shop is €14 for 4 glasses of Lanzarote volcanic wine. Choose from the Yaiza Board (4 Yaiza wines) or the Red Board (including Yaiza Tinto). Walk in any time — no booking needed."}},
              {"@type": "Question", "name": "What craft beer is available in Lanzarote?", "acceptedAnswer": {"@type": "Answer", "text": "Lanzarote has two main craft breweries: Malpeis (La Fresca Session IPA, Rofe Bock, Bermeja Red IPA) and NAO (Doble, Cactus Session, Lager, Hoppy, Marinera, Capitan)."}},
              {"@type": "Question", "name": "What is Yaiza wine and where can I buy it?", "acceptedAnswer": {"@type": "Answer", "text": "Yaiza wine is produced by Bodega Yaiza near Playa Blanca, famous for its distinctive blue bottles. Made from Malvasia grapes grown in volcanic ash pits (zocos) unique to Lanzarote's La Geria wine region. Buy at Brunchi.shop from €21.50/bottle or taste for €14 (4 glasses)."}},
              {"@type": "Question", "name": "Does Brunchi.shop sell spirits and whisky?", "acceptedAnswer": {"@type": "Answer", "text": "Yes! Brunchi.shop has one of the largest spirits collections in Playa Blanca with over 800 products including premium whisky, gin, rum, vodka, tequila, brandy and liqueurs. Whisky flight tasting for €29 and individual terrace measures from €4.50."}},
              {"@type": "Question", "name": "Is there parking at Marina Rubicon?", "acceptedAnswer": {"@type": "Answer", "text": "Yes! Marina Rubicon has underground parking beneath the Rubicon Shop & Fun centre. First 45 minutes free, extendable to nearly 3 hours free with a validated receipt. After that, rates are €0.02/minute (max €25/day). EV charging points available."}},
              {"@type": "Question", "name": "What is there to do at Marina Rubicon in the evening?", "acceptedAnswer": {"@type": "Answer", "text": "Marina Rubicon comes alive in the evening with terrace bars, restaurants and stunning sunset views. Visit Brunchi.shop for wine tasting with Motown music, enjoy sangria (Red Wine or Cava Royale), the best Aperol Spritz in Playa Blanca, or sample Lanzarote craft beers."}},
              {"@type": "Question", "name": "How do I get to Marina Rubicon from my hotel?", "acceptedAnswer": {"@type": "Answer", "text": "Walk along the seafront promenade (20–40 minutes from central Playa Blanca), take local bus number 30 (around €1.40), or taxi (under €10 from most hotels). Free underground parking for up to 45 minutes, extendable to nearly 3 hours free with a purchase receipt."}},
              {"@type": "Question", "name": "What are the best things to do in Playa Blanca in the evening?", "acceptedAnswer": {"@type": "Answer", "text": "Evening highlights: wine tasting at Brunchi.shop's terrace in Marina Rubicon, sunset dining at La Taberna or Jungle Vibes, strolling the Paseo Maritimo seafront promenade, the best sangria and Aperol Spritz in Playa Blanca at Brunchi, and watching boats at the harbour."}}
            ]
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Brunchi.shop",
            "url": "https://brunchi.shop",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://brunchi.shop/spirits?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "SiteNavigationElement",
            "name": ["Wines", "Yaiza Wines", "El Grifo Wines", "Bermejo Wines", "Spirits", "Beers", "Malpeis Beer", "NAO Beer", "Wine Tasting", "Local Products", "Blog", "FAQ", "Contact", "About"],
            "url": ["https://brunchi.shop/wines", "https://brunchi.shop/wines/yaiza", "https://brunchi.shop/wines/el-grifo", "https://brunchi.shop/wines/bermejo", "https://brunchi.shop/spirits", "https://brunchi.shop/beers", "https://brunchi.shop/beers/malpeis", "https://brunchi.shop/beers/nao", "https://brunchi.shop/wine-tasting", "https://brunchi.shop/local-products", "https://brunchi.shop/blog", "https://brunchi.shop/faq", "https://brunchi.shop/contact", "https://brunchi.shop/about"]
          },
          {
            "@context": "https://schema.org",
            "@type": "FoodEvent",
            "name": "Walk-In Wine Tasting at Brunchi.shop",
            "description": "Walk-in wine tasting Monday to Saturday — 4 glasses of Lanzarote volcanic wines for €14 on our sunny terrace at Marina Rubicon. Choose a Yaiza Board (Rosado, Seco, Semi-Dulce, Afrutado) or Red Board (Yaiza Tinto, Volcano, Ribera, Rioja). No booking needed.",
            "startDate": "2026-01-01",
            "endDate": "2026-12-31",
            "eventSchedule": {
              "@type": "Schedule",
              "repeatFrequency": "P1D",
              "byDay": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"],
              "startTime": "10:00",
              "endTime": "22:00"
            },
            "location": {
              "@type": "Place",
              "name": "Brunchi.shop — Marina Rubicon",
              "address": {"@type": "PostalAddress", "streetAddress": "Calle El Burrugo 44B", "addressLocality": "Playa Blanca", "postalCode": "35580", "addressCountry": "ES"},
              "geo": {"@type": "GeoCoordinates", "latitude": 28.8575, "longitude": -13.8169}
            },
            "offers": [
              {"@type": "Offer", "name": "Yaiza Board (White Tasting)", "description": "4 glasses: Rosado, Seco, Semi-Dulce, Afrutado", "price": "14.00", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "hasMerchantReturnPolicy": {"@id": "https://brunchi.shop/#return-policy"}, "validFrom": "2026-01-01", "url": "https://brunchi.shop/wine-tasting"},
              {"@type": "Offer", "name": "Red Board (Red Tasting)", "description": "4 glasses: Yaiza Tinto, Volcano, Ribera del Duero, Rioja", "price": "14.00", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "hasMerchantReturnPolicy": {"@id": "https://brunchi.shop/#return-policy"}, "validFrom": "2026-01-01", "url": "https://brunchi.shop/wine-tasting"}
            ],
            "performer": {"@type": "Person", "name": "Florin", "jobTitle": "Co-Owner"},
            "organizer": {"@type": "Organization", "name": "Brunchi.shop", "url": "https://brunchi.shop"},
            "typicalAgeRange": "18-",
            "maximumAttendeeCapacity": 20,
            "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
            "eventStatus": "https://schema.org/EventScheduled",
            "image": "https://customer-assets.emergentagent.com/job_6abba286-c4a7-450f-8f93-020d629f80c9/artifacts/zxttut82_7.png"
          },
          {
            "@context": "https://schema.org",
            "@type": "OfferCatalog",
            "name": "Brunchi.shop Special Offers",
            "itemListElement": [
              {"@type": "Offer", "name": "Wine Tasting — 4 Glasses", "description": "Choose a Yaiza Board (Rosado, Seco, Semi-Dulce, Afrutado) or Red Board (Tinto, Volcano, Ribera, Rioja) — mix and match welcome", "price": "14.00", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "hasMerchantReturnPolicy": {"@id": "https://brunchi.shop/#return-policy"}, "url": "https://brunchi.shop/wine-tasting", "itemOffered": {"@type": "Product", "image": "https://customer-assets.emergentagent.com/job_6abba286-c4a7-450f-8f93-020d629f80c9/artifacts/zxttut82_7.png", "name": "Lanzarote Wine Tasting", "description": "Walk-in wine tasting on the sunny terrace at Marina Rubicon — choose a Yaiza Board or Red Board, 4 glasses of Lanzarote volcanic wines for EUR 14. No booking needed.", "offers": {"@type": "Offer", "price": "14.00", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "hasMerchantReturnPolicy": {"@id": "https://brunchi.shop/#return-policy"}}, "sku": "BRUNCHI-WINETASTING-4", "brand": {"@type": "Brand", "name": "Brunchi.shop"}, "category": "Wine Tasting Experience", "aggregateRating": {"@type": "AggregateRating", "ratingValue": "5.0", "reviewCount": "44", "bestRating": "5", "worstRating": "1"}, "review": [{"@type": "Review", "author": {"@type": "Person", "name": "James W"}, "datePublished": "2025-11-20", "reviewRating": {"@type": "Rating", "ratingValue": "5", "bestRating": "5"}, "reviewBody": "Really relaxing atmosphere. Wine tasting on the terrace was the highlight of our holiday."}]}},
              {"@type": "Offer", "name": "Whisky Flight — 3 Measures", "description": "3 premium whisky measures with expert guidance from Florin", "price": "29.00", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "hasMerchantReturnPolicy": {"@id": "https://brunchi.shop/#return-policy"}, "url": "https://brunchi.shop/wine-tasting", "itemOffered": {"@type": "Product", "image": "https://customer-assets.emergentagent.com/job_6abba286-c4a7-450f-8f93-020d629f80c9/artifacts/zxttut82_7.png", "name": "Premium Whisky Flight", "description": "3 premium whisky measures with expert guidance from Florin. Japanese, Scottish, Irish and bourbon selections at Brunchi.shop.", "offers": {"@type": "Offer", "price": "29.00", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "hasMerchantReturnPolicy": {"@id": "https://brunchi.shop/#return-policy"}}, "sku": "BRUNCHI-WHISKYFLIGHT-3", "brand": {"@type": "Brand", "name": "Brunchi.shop"}, "category": "Whisky Tasting Experience", "aggregateRating": {"@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "52", "bestRating": "5", "worstRating": "1"}, "review": [{"@type": "Review", "author": {"@type": "Person", "name": "Rob W"}, "datePublished": "2025-09-16", "reviewRating": {"@type": "Rating", "ratingValue": "5", "bestRating": "5"}, "reviewBody": "The choice of drinks is incredible. Great bar/shop and really engaging and welcoming staff."}]}},
              {"@type": "Offer", "name": "Wine by the Glass", "description": "Yaiza, Volcano, Vinosol, Ribera, Rioja, Cava, Prosecco — served on the terrace", "price": "6.00", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "hasMerchantReturnPolicy": {"@id": "https://brunchi.shop/#return-policy"}, "url": "https://brunchi.shop/wine-tasting", "itemOffered": {"@type": "Product", "image": "https://customer-assets.emergentagent.com/job_6abba286-c4a7-450f-8f93-020d629f80c9/artifacts/zxttut82_7.png", "name": "Wine by the Glass", "description": "Lanzarote wines by the glass on the terrace — Yaiza, Volcano, Vinosol, Ribera del Duero, Rioja, Cava and Prosecco from EUR 6.", "offers": {"@type": "Offer", "price": "6.00", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "hasMerchantReturnPolicy": {"@id": "https://brunchi.shop/#return-policy"}}, "sku": "BRUNCHI-WINEGLASS", "brand": {"@type": "Brand", "name": "Brunchi.shop"}, "category": "Wine", "aggregateRating": {"@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "52", "bestRating": "5", "worstRating": "1"}, "review": [{"@type": "Review", "author": {"@type": "Person", "name": "James W"}, "datePublished": "2025-11-20", "reviewRating": {"@type": "Rating", "ratingValue": "5", "bestRating": "5"}, "reviewBody": "Wine tasting of the local Yaiza wine, the famous blue bottles. Great value at 14 euros for 4 glasses."}]}}
            ]
          },
        ]}
      />
      <section className="relative h-screen flex items-center justify-center text-white">
        {/* Speakable voice search answers — hidden visually, visible to assistants */}
        <div className="speakable-answer sr-only" aria-hidden="false">
          <p>Brunchi.shop is a family-run wine, spirits and craft beer shop with a terrace bar at Marina Rubicon, Playa Blanca, Lanzarote.</p>
          <p>We offer walk-in wine tastings Monday to Saturday for 14 euros — four glasses of Lanzarote volcanic wines on our sunny terrace. No booking needed.</p>
          <p>We stock over 800 spirits, 100 wines including all major Lanzarote bodegas, and local Malpeis and NAO craft beers from 4 euros 50.</p>
          <p>Buy Cuban cigars including Cohiba, Montecristo and Romeo y Julieta from our climate-controlled humidor.</p>
          <p>Questions? Email consultas@brunchi.shop or walk in during opening hours.</p>
          <p>Artisan ice cream, soft drinks and family-friendly terrace. Open Monday to Saturday 10am-3pm and 6pm-10pm (closed Sundays).</p>
        </div>
        <MultilingualSpeakable pageKey="home" />
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url('${IMAGES.hero}')`
          }}
        />
        <div className="relative z-10 text-center container mx-auto px-4">
          <p className="text-5xl sm:text-6xl md:text-8xl font-bold mb-6 break-words" role="heading" aria-level="1">Brunchi.shop</p>
          <h2 className="text-3xl md:text-5xl mb-8">Yaiza Wine Tasting, Malpeis & NAO Craft Beer</h2>
          <p className="text-xl md:text-2xl mb-8 max-w-4xl mx-auto">
            Experience authentic Lanzarote wine tasting on our sunny terrace overlooking Marina Rubicón.
            Walk in any time during opening hours, no booking needed.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-lg px-8 py-4" asChild>
              <Link to="/wine-tasting">Wine Tasting</Link>
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600 text-lg px-8 py-4" asChild>
              <Link to="/contact">Find Us</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* What We Offer Section */}
      <section className="py-8 bg-gradient-to-r from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-6">
            <h2 className="text-4xl font-bold mb-4 text-blue-600">What We Offer</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From volcanic wines and local craft beers to premium spirits and terrace drinks - 
              discover the wines, spirits, beers and local flavours of Lanzarote.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="hover:shadow-xl transition-shadow duration-300 flex flex-col h-full">
              <CardHeader className="flex-grow">
                <div className="w-full h-48 rounded-lg mb-4 bg-red-900 flex items-center justify-center overflow-hidden">
                  <img loading="lazy" decoding="async" 
                    src="https://customer-assets.emergentagent.com/job_6abba286-c4a7-450f-8f93-020d629f80c9/artifacts/zxttut82_7.png"
                    alt="Lanzarote red wine tasting at Brunchi — four glasses of Yaiza, Vulcano, Rioja and Ribera del Duero volcanic wines"
                    title="Red Wine Tasting at Brunchi, Marina Rubicon"
                    width="400" height="192"
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <CardTitle className="text-blue-600">Yaiza Wine & Volcanic Wines</CardTitle>
                <CardDescription>
                  Iconic Yaiza wines and premium selections from La Geria, El Grifo, and Bermejo. 
                  Taste volcanic terroir in every glass.
                </CardDescription>
              </CardHeader>
              <CardContent className="mt-auto">
                <div className="space-y-2 mb-4">
                  <p className="text-sm font-semibold">Yaiza wines: from €21.50 per bottle</p>
                  <p className="text-sm">Other wines: Prices vary</p>
                  <p className="text-sm">€6 per glass</p>
                  <p className="text-sm">Wine tasting: 4 glasses €14</p>
                </div>
                <div className="flex gap-2">
                  <Button asChild className="flex-1 bg-blue-600 hover:bg-blue-700">
                    <Link to="/wines" data-testid="explore-wines">Explore Wines</Link>
                  </Button>
                  <Button asChild variant="outline" className="flex-1 border-amber-600 text-amber-600 hover:bg-amber-50">
                    <Link to="/wine-tasting" data-testid="wine-tasting-link">Tasting €14</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-xl transition-shadow duration-300 flex flex-col h-full">
              <CardHeader className="flex-grow">
                <div className="w-full h-48 rounded-lg mb-4 bg-gray-100 flex items-center justify-center overflow-hidden">
                  <img loading="lazy" decoding="async" 
                    src={IMAGES.fridgeBeer}
                    alt="Brunchi craft beer fridge with Malpeis and NAO Lanzarote brewery beers — Despiste, La Fresca, Bermeja IPA and more"
                    title="Craft Beer Selection at Brunchi, Playa Blanca"
                    width="400" height="192"
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <CardTitle className="text-blue-600">Malpeis & NAO Craft Beers</CardTitle>
                <CardDescription>
                  Malpeis and NAO brewery selections plus Spanish craft favorites. 
                  From hoppy IPAs to rich bocks and refreshing session ales.
                </CardDescription>
              </CardHeader>
              <CardContent className="mt-auto">
                <div className="space-y-2 mb-4">
                  <p className="text-sm">Malpeis &amp; NAO €5.00 per bottle</p>
                  <p className="text-sm font-semibold">Spanish beers €3.50 · Speciality beers from €5.50</p>
                </div>
                <Button asChild className="w-full bg-blue-600 hover:bg-blue-700">
                  <Link to="/beers">View Craft Beer Range</Link>
                </Button>
              </CardContent>
            </Card>
            
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-8 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${IMAGES.wineSelection}')`,
          }}
        />
        <div className="absolute inset-0 bg-white/70"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div ref={ref1}>
              <p className="text-4xl font-bold mb-2 text-blue-600">{count1}+</p>
              <p className="text-gray-800 font-medium">WINES STOCKED<br/>INCLUDING RARE & LIMITED<br/>EDITION LANZAROTE BOTTLES</p>
            </div>
            <div ref={ref2}>
              <p className="text-4xl font-bold mb-2 text-blue-600">{count2}+</p>
              <p className="text-gray-800 font-medium">CRAFT BEERS<br/>LANZAROTE & CANARY ISLAND<br/>BREWED<br/>CRAFT BEER</p>
            </div>
            <div ref={ref3}>
              <p className="text-4xl font-bold mb-2 text-blue-600">{count3}+</p>
              <p className="text-gray-800 font-medium">VISITORS FROM<br/>OVER 25 COUNTRIES HAVE<br/>SHOPPED WITH US</p>
            </div>
            <div ref={ref4}>
              <p className="text-4xl font-bold mb-2 text-blue-600">{count4}</p>
              <p className="text-gray-800 font-medium">HAPPY CUSTOMERS AND<br/>COUNTING!</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Picks - Wine of Week & Staff Pick */}
      <FeaturedPicks />

      {/* Reviews Section */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-4">
            <p className="text-sm uppercase tracking-wide text-gray-500 mb-2">CUSTOMER REVIEWS</p>
            <h2 className="text-4xl font-bold mb-6 text-gray-800">What Our Customers Are Saying</h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto mb-8">
              We're proud to be a trusted favourite for both locals and visitors in Playa Blanca. 
              From sunset wine tastings to our customers love the quality, care and 
              convenience that Brunchi brings. Here's what a few of them had to say.
            </p>
          </div>

          {/* Google Rating */}
          <div className="flex flex-col sm:flex-row items-center justify-between mb-8 gap-4">
            <div className="flex flex-wrap items-center justify-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-blue-600">G</span>
                <span className="text-red-500 text-2xl font-bold">o</span>
                <span className="text-yellow-500 text-2xl font-bold">o</span>
                <span className="text-blue-600 text-2xl font-bold">g</span>
                <span className="text-green-500 text-2xl font-bold">l</span>
                <span className="text-red-500 text-2xl font-bold">e</span>
                <span className="text-gray-700 ml-2">Rating</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-2xl font-bold">4.9</span>
                <div className="flex text-yellow-400">
                  {"★★★★★".split("").map((star, i) => (
                    <span key={i} className="text-lg">{star}</span>
                  ))}
                </div>
                <span className="text-gray-600">Based on 52 Google Reviews</span>
              </div>
            </div>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" asChild>
              <a href="https://g.page/r/Ca1Dv59sJTIuEBM/review" target="_blank" rel="noopener noreferrer">
                Write a Review
              </a>
            </Button>
          </div>
          
          {/* Individual Review Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-white p-6" data-testid="review-card-ange">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  A
                </div>
                <div>
                  <p className="font-semibold">Ange L</p>
                  <div className="flex text-yellow-400 mb-1">
                    {"★★★★★".split("").map((star, i) => (
                      <span key={i} className="text-sm">{star}</span>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">December 7, 2025</p>
                </div>
              </div>
              <p className="text-gray-700 text-sm">
                We are regulars at Brunchi and for good reason. It is a calm little corner of Marina Rubicon where you can try and buy Lanzarote wines without any pressure, just good guidance and a relaxed vibe. The owners are genuinely lovely and have a natural way of making you feel welcome every time.
              </p>
            </Card>

            <Card className="bg-white p-6" data-testid="review-card-james">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  J
                </div>
                <div>
                  <p className="font-semibold">James W</p>
                  <div className="flex text-yellow-400 mb-1">
                    {"★★★★★".split("").map((star, i) => (
                      <span key={i} className="text-sm">{star}</span>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">November 20, 2025</p>
                </div>
              </div>
              <p className="text-gray-700 text-sm">
                Really relaxing atmosphere and comfortable seating. I've been looking for a place in Playa Blanca which does wine tasting for the last couple of trips. The wine tasting of the local Yaiza wine, the famous blue bottles, is great value. You can do a white and rose or a red wine tasting.
              </p>
            </Card>

            <Card className="bg-white p-6" data-testid="review-card-rob">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  R
                </div>
                <div>
                  <p className="font-semibold">Rob W</p>
                  <div className="flex text-yellow-400 mb-1">
                    {"★★★★★".split("").map((star, i) => (
                      <span key={i} className="text-sm">{star}</span>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">September 16, 2025</p>
                </div>
              </div>
              <p className="text-gray-700 text-sm">
                Great bar/shop. Take time to visit because you will not be disappointed. Florin, the owner, is a really engaging person and welcoming. The choice of drinks is incredible. Florin's bar became our local and we will definitely be back.
              </p>
            </Card>

            <Card className="bg-white p-6" data-testid="review-card-emilia">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  E
                </div>
                <div>
                  <p className="font-semibold">Emilia</p>
                  <div className="flex text-yellow-400 mb-1">
                    {"★★★★★".split("").map((star, i) => (
                      <span key={i} className="text-sm">{star}</span>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">June 29, 2025</p>
                </div>
              </div>
              <p className="text-gray-700 text-sm">
                A great little shop. We stopped off here on market day to have a sit down and a drink in the little cafe section. The service was very quick and very friendly. Plenty of bottled drink options to choose from, which would make good gifts. Thank you!
              </p>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
            <Card className="bg-white p-6" data-testid="review-card-carol">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  C
                </div>
                <div>
                  <p className="font-semibold">Carol S</p>
                  <div className="flex text-yellow-400 mb-1">
                    {"★★★★★".split("").map((star, i) => (
                      <span key={i} className="text-sm">{star}</span>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">March 20, 2025</p>
                </div>
              </div>
              <p className="text-gray-700 text-sm">
                Amazing little place with a fantastic range of wines and beers. The owner is friendly and knowledgeable. A perfect way to chill out and chat to new friends on comfy sofas in the sunshine. A must visit when in Playa Blanca!
              </p>
            </Card>

            <Card className="bg-white p-6" data-testid="review-card-aine">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  A
                </div>
                <div>
                  <p className="font-semibold">Aine Kiely</p>
                  <div className="flex text-yellow-400 mb-1">
                    {"★★★★★".split("").map((star, i) => (
                      <span key={i} className="text-sm">{star}</span>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">Google Review</p>
                </div>
              </div>
              <p className="text-gray-700 text-sm">
                Wonderfully friendly staff, great selection and prices and a fabulous atmosphere.
              </p>
            </Card>

            <Card className="bg-white p-6" data-testid="review-card-kirsten">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  K
                </div>
                <div>
                  <p className="font-semibold">Kirsten Sproul-Brown</p>
                  <div className="flex text-yellow-400 mb-1">
                    {"★★★★★".split("").map((star, i) => (
                      <span key={i} className="text-sm">{star}</span>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">Google Review</p>
                </div>
              </div>
              <p className="text-gray-700 text-sm">
                Excellent service and the wine flight was such good value for money.
              </p>
            </Card>

            <Card className="bg-white p-6" data-testid="review-card-stephen">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  S
                </div>
                <div>
                  <p className="font-semibold">Stephen Green</p>
                  <div className="flex text-yellow-400 mb-1">
                    {"★★★★★".split("").map((star, i) => (
                      <span key={i} className="text-sm">{star}</span>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">Google Review</p>
                </div>
              </div>
              <p className="text-gray-700 text-sm">
                Tasting wines a good choice, great craft beers.
              </p>
            </Card>
          </div>

          <div className="text-center mt-8 flex flex-wrap justify-center gap-3">
            <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white" asChild>
              <a href="https://search.google.com/local/reviews?placeid=ChIJ-zP4egMxRgwRrUO_n2wlMi4" target="_blank" rel="noopener noreferrer">
                View All 52 Google Reviews
              </a>
            </Button>
            <Button variant="outline" className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white" asChild>
              <a href="https://www.tripadvisor.com/Restaurant_Review-g659633-d28127915-Reviews-Brunchi-Playa_Blanca_Lanzarote_Canary_Islands.html" target="_blank" rel="noopener noreferrer">
                Find Us on TripAdvisor
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Review CTA Banner */}
      <GoogleReviewCTA variant="banner" />

      {/* FAQ Section - Marina Rubicon & Playa Blanca Guide */}
      <section className="py-12 bg-white" data-testid="faq-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4 text-gray-800">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Your guide to Marina Rubicon, Playa Blanca and everything Brunchi
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto space-y-4">
            <details className="bg-gray-50 rounded-lg p-5 cursor-pointer group" data-testid="faq-wine-shop">
              <summary className="font-semibold text-gray-800 text-lg flex justify-between items-center">
                Where can I buy wine in Playa Blanca, Lanzarote?
                <span className="text-blue-600 group-open:rotate-45 transition-transform text-2xl">+</span>
              </summary>
              <p className="mt-3 text-gray-700 leading-relaxed">
                Brunchi.shop at Marina Rubicon is the best wine shop in Playa Blanca, offering over 100 wines including iconic Yaiza blue-bottle wines (from €21.50/bottle), El Grifo, Bermejo and La Geria volcanic wines. We offer walk-in wine tasting — 4 small glasses for just €14. We also serve a selection by the glass including Yaiza, Volcano, Vinosol, Ribera, Rioja, Cava and Prosecco. Open Monday to Saturday 10am-3pm and 6pm-10pm, closed Sundays.
              </p>
            </details>

            <details className="bg-gray-50 rounded-lg p-5 cursor-pointer group" data-testid="faq-market-days">
              <summary className="font-semibold text-gray-800 text-lg flex justify-between items-center">
                What days is Marina Rubicon market in Playa Blanca?
                <span className="text-blue-600 group-open:rotate-45 transition-transform text-2xl">+</span>
              </summary>
              <p className="mt-3 text-gray-700 leading-relaxed">
                The Marina Rubicon market runs every <strong>Wednesday and Saturday from 9:00 AM to 2:00 PM</strong>. You'll find handmade crafts, jewellery, leather goods, clothing, local wines, aloe vera products and souvenirs along the seafront promenade. Arrive early for the best selection. Brunchi.shop is open on market days and every other day too — pop in for a wine tasting after browsing the stalls!
              </p>
            </details>

            <details className="bg-gray-50 rounded-lg p-5 cursor-pointer group" data-testid="faq-parking">
              <summary className="font-semibold text-gray-800 text-lg flex justify-between items-center">
                Is there parking at Marina Rubicon?
                <span className="text-blue-600 group-open:rotate-45 transition-transform text-2xl">+</span>
              </summary>
              <p className="mt-3 text-gray-700 leading-relaxed">
                Yes! Marina Rubicon has underground parking beneath the Rubicon Shop & Fun centre. The first 45 minutes are free, and you can extend to nearly 3 hours free with a validated receipt from any marina business. After that, rates are just €0.02/minute (max €25/day). On market days (Wednesday & Saturday) it fills up quickly, so arrive early or find street parking nearby. EV charging points are also available.
              </p>
            </details>

            <details className="bg-gray-50 rounded-lg p-5 cursor-pointer group" data-testid="faq-evening">
              <summary className="font-semibold text-gray-800 text-lg flex justify-between items-center">
                What is there to do at Marina Rubicon in the evening?
                <span className="text-blue-600 group-open:rotate-45 transition-transform text-2xl">+</span>
              </summary>
              <p className="mt-3 text-gray-700 leading-relaxed">
                Marina Rubicon comes alive in the evening with terrace bars, restaurants and stunning sunset views over the harbour. Visit Brunchi.shop for wine tasting on the terrace with Motown music, enjoy our famous sangria — Red Wine or Cava Royale — by the glass or jug, the best Aperol Spritz in Playa Blanca, or sample Lanzarote craft beers. The promenade is perfect for an evening stroll watching luxury yachts. For food, try La Taberna right next door or Jungle Vibes for a vibrant atmosphere.
              </p>
            </details>

            <details className="bg-gray-50 rounded-lg p-5 cursor-pointer group" data-testid="faq-wine-tasting">
              <summary className="font-semibold text-gray-800 text-lg flex justify-between items-center">
                Where can I do wine tasting in Playa Blanca without a tour?
                <span className="text-blue-600 group-open:rotate-45 transition-transform text-2xl">+</span>
              </summary>
              <p className="mt-3 text-gray-700 leading-relaxed">
                Brunchi.shop at Marina Rubicon offers walk-in wine tasting every day — no tour or booking needed. For €14 you get 4 small glasses of Lanzarote wines. Choose white (Rosé, Semi-Dulce, Seco, Afrutado) or red (Yaiza, Vulcano, Rioja, Ribera). Enjoy your tasting on our sunny terrace overlooking the marina. We also serve Yaiza, Volcano, Vinosol, Ribera, Rioja, Cava and Prosecco by the glass.
              </p>
            </details>

            <details className="bg-gray-50 rounded-lg p-5 cursor-pointer group" data-testid="faq-craft-beer">
              <summary className="font-semibold text-gray-800 text-lg flex justify-between items-center">
                What craft beer is available in Lanzarote?
                <span className="text-blue-600 group-open:rotate-45 transition-transform text-2xl">+</span>
              </summary>
              <p className="mt-3 text-gray-700 leading-relaxed">
                Lanzarote has two main craft breweries: <strong>Malpeis</strong> (based in Tinajo) brewing La Fresca Session IPA, Rofe Bock, and Bermeja Red IPA; and <strong>NAO</strong> producing Doble, Cactus Session, Lager, Hoppy, Marinera and Capitan. We also carry Belgian, Spanish and Tenerife craft beers.
              </p>
            </details>

            <details className="bg-gray-50 rounded-lg p-5 cursor-pointer group" data-testid="faq-shops">
              <summary className="font-semibold text-gray-800 text-lg flex justify-between items-center">
                What shops are at Marina Rubicon, Playa Blanca?
                <span className="text-blue-600 group-open:rotate-45 transition-transform text-2xl">+</span>
              </summary>
              <p className="mt-3 text-gray-700 leading-relaxed">
                Marina Rubicon features a variety of shops and boutiques along the promenade including Brunchi.shop (wines, spirits, craft beer, gifts), Fund Grube (designer handbags, sunglasses), plus clothing boutiques, jewellery shops, souvenir stores, and the Lasal Shopping Strip for upscale retail. The Rubicon Shop & Fun centre has additional shops. Most are open 10am–10pm daily.
              </p>
            </details>

            <details className="bg-gray-50 rounded-lg p-5 cursor-pointer group" data-testid="faq-yaiza">
              <summary className="font-semibold text-gray-800 text-lg flex justify-between items-center">
                What is Yaiza wine and where can I buy it?
                <span className="text-blue-600 group-open:rotate-45 transition-transform text-2xl">+</span>
              </summary>
              <p className="mt-3 text-gray-700 leading-relaxed">
                Yaiza wine is produced by Bodega Yaiza near Playa Blanca, Lanzarote, famous for its distinctive blue bottles. The wine is made from Malvasia grapes grown in volcanic ash pits (called zocos) unique to Lanzarote's La Geria wine region — a UNESCO-protected landscape. You can buy Yaiza wine at Brunchi.shop in Marina Rubicon from €21.50 per bottle (blue bottles) or €25 for the Tinto. Yaiza is also available by the glass on the terrace. We offer a wine tasting for €14 — choose the Yaiza Board (Rosado, Seco, Semi-Dulce, Afrutado) or the Red Board (Tinto, Volcano, Ribera, Rioja), or mix and match.
              </p>
            </details>

            <details className="bg-gray-50 rounded-lg p-5 cursor-pointer group" data-testid="faq-spirits">
              <summary className="font-semibold text-gray-800 text-lg flex justify-between items-center">
                Does Brunchi.shop sell spirits and whisky?
                <span className="text-blue-600 group-open:rotate-45 transition-transform text-2xl">+</span>
              </summary>
              <p className="mt-3 text-gray-700 leading-relaxed">
                Yes! Brunchi.shop has one of the largest spirits collections in Playa Blanca with over 800 products. We stock premium whisky (Scotch, Irish, Japanese, Bourbon), gin, rum (including rare Canarian rums like Arehucas), vodka, tequila, brandy, and liqueurs. We also offer a whisky flight tasting for €29 and individual terrace measures from €4.50 on our sunny terrace.
              </p>
            </details>

            <details className="bg-gray-50 rounded-lg p-5 cursor-pointer group" data-testid="faq-getting-there">
              <summary className="font-semibold text-gray-800 text-lg flex justify-between items-center">
                How do I get to Marina Rubicon from my hotel?
                <span className="text-blue-600 group-open:rotate-45 transition-transform text-2xl">+</span>
              </summary>
              <p className="mt-3 text-gray-700 leading-relaxed">
                Marina Rubicon is at the southern end of Playa Blanca. You can walk along the seafront promenade (20–40 minutes from central Playa Blanca), take local bus number 30 (around €1.40), or taxi (under €10 from most hotels). There's free underground parking for up to 45 minutes, extendable to nearly 3 hours free with a purchase receipt. The marina is well signposted from all parts of Playa Blanca.
              </p>
            </details>

            <details className="bg-gray-50 rounded-lg p-5 cursor-pointer group" data-testid="faq-evening-things">
              <summary className="font-semibold text-gray-800 text-lg flex justify-between items-center">
                What are the best things to do in Playa Blanca in the evening?
                <span className="text-blue-600 group-open:rotate-45 transition-transform text-2xl">+</span>
              </summary>
              <p className="mt-3 text-gray-700 leading-relaxed">
                Evening activities in Playa Blanca include: wine tasting at Brunchi.shop's terrace in Marina Rubicon; sunset dining at La Taberna or Jungle Vibes; strolling the seafront promenade (Paseo Maritimo); enjoying the best sangria in Playa Blanca (Red Wine or Cava Royale) or Aperol Spritz at Brunchi's terrace; browsing the marina's evening-lit boutiques; and watching the boats come in at the harbour. The atmosphere is relaxed and perfect for couples and families alike.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <PhotoGallery />
        </div>
      </section>
    </div>
  );
};

// About Page Component  
const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white pt-16">
      {/* Hero Section */}
      <section className="relative py-24 flex items-center justify-center text-white">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url('https://images.unsplash.com/photo-1541777594744-addc2de9d110?w=1920&h=800&fit=crop')`
          }}
        />
        <div className="relative z-10 text-center container mx-auto px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">About Brunchi</h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            More than a shop – it's a shared passion for local flavours, global inspiration, and personal touch.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center mb-6">
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold mb-4 text-blue-600">Who Are We?</h2>
              <p className="text-lg text-gray-700 mb-4">
                Brunchi is a cosy boutique shop in the heart of Marina Rubicón, Playa Blanca, specialising in wine, 
                craft beer and spirits. Our intimate terrace seats just 15, making it the perfect spot to 
                relax with a drink, try a wine tasting, or sample a Lanzarote craft beer.
              </p>
              <p className="text-lg text-gray-700 mb-4">
                Whether you pop in for a tasting or a bottle to take home, everything at Brunchi is chosen by us to show off the 
                flavours, craftsmanship, and charm of Lanzarote and the Canary Islands. We love making experiences easy, 
                special, and full of meaning.
              </p>
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="font-bold text-xl mb-3 text-blue-600">Local Flavours. Global Inspiration. Personal Touch.</h3>
                <p className="text-gray-700">
                  Our collection celebrates Lanzarote's finest; volcanic wines, local craft beers, and Canary Island 
                  spirits like honey rum and caramel vodka. Each bottle is chosen for its story and taste.
                </p>
              </div>
            </div>
            <div>
              <img loading="lazy" decoding="async" 
                src={IMAGES.cocktails}
                alt="Brunchi terrace bar with sunset views at Marina Rubicon — wine, craft beer and spirits served daily"
                title="Brunchi Terrace Bar — Marina Rubicon Sunset"
                width="400" height="400"
                className="rounded-lg shadow-lg w-full"
              />
            </div>
          </div>

          {/* Team Section */}
          <div className="mb-6" data-testid="about-team-section">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
              <div>
                <img loading="lazy" decoding="async" 
                  src={IMAGES.teamPhoto}
                  alt="Florin and Jo, founders of Brunchi wine and spirits shop — family-run business at Marina Rubicon, Playa Blanca since 2024"
                  title="Meet Florin & Jo — Brunchi Founders"
                  width="400" height="400"
                  className="rounded-lg shadow-lg w-full"
                />
              </div>
              <div className="lg:col-span-2">
                <h2 className="text-4xl font-bold mb-2 text-blue-600">Meet Florin & Jo</h2>
                <p className="text-xl text-gray-600 mb-4">We Bring a Taste of the Island to Every Visit</p>
                
                <p className="text-lg text-gray-700 mb-4">
                  Founded by Florin & Jo, Brunchi has been part of Marina Rubicón since March 2024. We created Brunchi 
                  to share our favourite finds from Lanzarote, whether that's a glass of local wine, a craft beer, or a 
                  refreshing soft drink and ice cream on a busy market day.
                </p>
                <p className="text-lg text-gray-700 mb-4">
                  <strong>Florin</strong> is a trained chef with a real passion for great food and drink. He's got an amazing 
                  eye and taste for quality, always picking out the best bottles for our shelves.
                </p>
                <p className="text-lg text-gray-700 mb-6">
                  <strong>Jo</strong> is the creative eye behind Brunchi's style. She takes care of everything from gift 
                  wrapping and digital content to customer care, with a real knack for those thoughtful details.
                </p>
                <Button asChild className="bg-blue-600 hover:bg-blue-700">
                  <a href="mailto:consultas@brunchi.shop?subject=Brunchi.shop%20enquiry" data-testid="about-team-contact-btn">Get in Touch</a>
                </Button>
              </div>
            </div>
          </div>

          {/* Product Gallery */}
          <ProductGallery />
        </div>
      </section>
    </div>
  );
};

// Footer Component with Emergent branding moved here
const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white py-8">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <p className="text-2xl font-bold mb-4 text-blue-400">Brunchi.shop</p>
            <p className="text-gray-300 mb-4">
              Lanzarote's premier destination for craft beers, fine wines, and premium spirits. 
              Located in Marina Rubicón, Playa Blanca.
            </p>
          </div>
          
          <div>
            <p className="text-lg font-semibold mb-4">Quick Links</p>
            <div className="space-y-2 text-gray-300">
              <Link to="/about" className="block hover:text-blue-400">About Us</Link>
              <Link to="/wine-tasting" className="block hover:text-blue-400">Wine Tasting €14</Link>
              <Link to="/wines" className="block hover:text-blue-400">Wines</Link>
              <Link to="/beers" className="block hover:text-blue-400">Craft Beers</Link>
              <Link to="/spirits" className="block hover:text-blue-400">Products</Link>
              <Link to="/blog" className="block hover:text-blue-400">Blog</Link>
              <Link to="/products" className="block hover:text-blue-400" data-testid="footer-products-link">Shop All Products</Link>
              <Link to="/faq" className="block hover:text-blue-400">FAQ & Playa Blanca Guide</Link>
            </div>
          </div>
          
          <div>
            <p className="text-lg font-semibold mb-4">Contact Info</p>
            <div className="space-y-2 text-gray-300">
              <p>Calle El Burrugo 44B</p>
              <p>Marina Rubicón, Playa Blanca</p>
              <p>Lanzarote 35580</p>
              <p>Email: <a href="mailto:consultas@brunchi.shop" className="hover:text-blue-400">consultas@brunchi.shop</a></p>
            </div>
            <div className="flex space-x-4 mt-4 text-sm text-gray-400">
              <Link to="/privacy-policy" className="hover:text-blue-400">Privacy</Link>
              <Link to="/refund-policy" className="hover:text-blue-400">Refunds</Link>
              <Link to="/terms" className="hover:text-blue-400">Terms</Link>
            </div>
          </div>
          
          <div>
            <p className="text-lg font-semibold mb-4">Follow Us</p>
            <div className="flex space-x-4 mb-4">
              <a href="https://www.facebook.com/brunchi.shop" target="_blank" rel="noopener noreferrer"
                 className="text-blue-400 hover:text-blue-300 text-2xl" aria-label="Facebook">
                👍
              </a>
              <a href="https://www.tiktok.com/@brunchi.shop" target="_blank" rel="noopener noreferrer"
                 className="text-gray-300 hover:text-white text-2xl" aria-label="TikTok">
                🎵
              </a>
              <a href="mailto:consultas@brunchi.shop?subject=Brunchi.shop%20enquiry" 
                 className="text-green-400 hover:text-green-300 text-2xl" aria-label="Email">
                ✉️
              </a>
            </div>
            <div className="mb-4 flex flex-wrap gap-2">
              <a href="https://g.page/r/Ca1Dv59sJTIuEBM/review" target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg transition-colors">
                Leave a Google Review
              </a>
              <a href="https://www.tripadvisor.com/Restaurant_Review-g659633-d28127915-Reviews-Brunchi-Playa_Blanca_Lanzarote_Canary_Islands.html" target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-xs rounded-lg transition-colors">
                Review on TripAdvisor
              </a>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('showNewsletterWidget'))}
                className="inline-flex items-center px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs rounded-lg transition-colors"
              >
                Subscribe to Newsletter
              </button>
            </div>
            <div>
              <p className="text-sm text-gray-400 mb-1">Opening Hours:</p>
              <p className="text-sm text-gray-300">Mon-Sat 10am–3pm & 6pm–10pm</p>
              <p className="text-sm text-gray-300">Closed Sunday</p>
              <p className="text-sm text-gray-400 mt-1">Check Google before visiting, in case we need to close at short notice</p>
            </div>
          </div>
        </div>
        
        <Separator className="my-8 bg-gray-700" />
        
        <div className="flex flex-col md:flex-row justify-between items-center text-gray-400 text-sm">
          <p>&copy; 2025 Brunchi.shop. All rights reserved.</p>
          <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4 md:mt-0">
            <Link to="/privacy-policy" className="hover:text-blue-400">Privacy Policy</Link>
            <Link to="/refund-policy" className="hover:text-blue-400">Refund Policy</Link>
            <Link to="/terms" className="hover:text-blue-400">Terms of Service</Link>
            <Link to="/admin" className="hover:text-blue-400 text-xs opacity-50">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Layout wrapper that hides website chrome on admin/POS routes
const AppLayout = () => {
  const location = useLocation();
  const isAppRoute = location.pathname.startsWith('/admin') || location.pathname.startsWith('/pos') || location.pathname.startsWith('/receipt') || location.pathname.startsWith('/gift-card');

  // CRITICAL: Detect session_id synchronously BEFORE any route renders
  // This prevents race conditions with useEffect-based auth checks
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      {!isAppRoute && <SeasonalTicker />}
      {!isAppRoute && <Navigation />}
      {!isAppRoute && <BookDownloadPopup />}
      {!isAppRoute && <CookieConsentAndAgeVerification />}
      {!isAppRoute && <NewsletterWidget />}
      {!isAppRoute && <PageTranslator />}
      {!isAppRoute && <MainLangSwitch />}
      <main className={isAppRoute ? '' : 'pt-[104px] sm:pt-[104px]'}>
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          {SITE_LANGS.flatMap(l => [
            ['', <HomePage />], ['/about', <AboutPage />], ['/wines', <WinesPage />], ['/wines/:brandSlug', <WineBrandPage />],
            ['/wine-tasting', <WineTastingPage />], ['/beers', <BeersPage />], ['/beers/:brandSlug', <BeerBrandPage />],
            ['/spirits', <SpiritsPage />], ['/contact', <ContactPage />],
            ['/blog', <BlogHub />], ['/faq', <FAQPage />],
            ['/local-products', <LocalProductsPage />], ['/privacy-policy', <PrivacyPolicy />], ['/terms', <TermsConditions />], ['/refund-policy', <RefundPolicy />]
          ].map(([p, el]) => <Route key={`${l}${p}`} path={`/${l}${p}`} element={el} />))}
          <Route path="/about" element={<AboutPage />} />
          <Route path="/wines" element={<WinesPage />} />
          <Route path="/wines/:brandSlug" element={<WineBrandPage />} />
          <Route path="/wine-tasting" element={<WineTastingPage />} />
          <Route path="/beers" element={<BeersPage />} />
          <Route path="/beers/:brandSlug" element={<BeerBrandPage />} />
          <Route path="/spirits" element={<SpiritsPage />} />
          <Route path="/gifts" element={<Navigate to="/spirits" replace />} />
          <Route path="/hospitality" element={<Navigate to="/wine-tasting" replace />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/blog" element={<BlogHub />} />
          <Route path="/blog/tf-brewn-ipa-craft-beer-tenerife" element={<Navigate to="/blog/tf-brewn-ipa-craft-beer-lanzarote" replace />} />
          <Route path="/blog/:articleId" element={<BlogHub />} />
          {SITE_LANGS.map(l => <Route key={l} path={`/${l}/blog/:slug`} element={<TranslatedBlogPost />} />)}
          <Route path="/hotel-gifts" element={<Navigate to="/spirits" replace />} />
          {SITE_LANGS.flatMap(l => [['/gifts', '/spirits'], ['/build-hamper', '/spirits'], ['/hotel-gifts', '/spirits'], ['/hospitality', '/wine-tasting']].map(([from, to]) => (
            <Route key={`${l}${from}`} path={`/${l}${from}`} element={<Navigate to={`/${l}${to}`} replace />} />
          )))}
          <Route path="/build-hamper" element={<Navigate to="/spirits" replace />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/products" element={<ProductsIndex />} />
          <Route path="/product/:slug" element={<ProductPage />} />
          <Route path="/local-products" element={<LocalProductsPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/admin" element={<SecureAdminPanel />} />
          <Route path="/pos" element={<POSDashboard />} />
          <Route path="/receipt/:receiptId" element={<ReceiptPage />} />
          <Route path="/gift-card/:cardId" element={<GiftCardPage />} />
          {/* 301 Redirects for old/duplicate URLs (Google Search Console fixes) */}
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/home/" element={<Navigate to="/" replace />} />
          <Route path="/shop-2" element={<Navigate to="/spirits" replace />} />
          <Route path="/shop-2/" element={<Navigate to="/spirits" replace />} />
          <Route path="/project/*" element={<Navigate to="/" replace />} />
          {/* Catch-all: redirect unknown routes to home */}
          {SITE_LANGS.flatMap(l => [
            <Route key={`${l}-products`} path={`/${l}/products`} element={<ProductsIndex />} />,
            <Route key={`${l}-product`} path={`/${l}/product/:slug`} element={<ProductPage />} />
          ])}
          {SITE_LANGS.map(l => <Route key={`${l}-fallback`} path={`/${l}/*`} element={<LangFallback />} />)}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </main>
      {!isAppRoute && <TrustBadges />}
      {!isAppRoute && <Footer />}
      <ChatWidget />
      {!isAppRoute && <StickyCtaBar />}
    </>
  );
};

// Main App Component
export default function App() {
  return (
    <div className="App">
      <Toaster />
      <BrowserRouter>
        <AppLayout />
      </BrowserRouter>
    </div>
  );
}